import type { Graphics } from "pixi.js";
import type { ProjectileVisualProfile } from "../champions/projectileVisuals.js";

const CORE_LENGTH_RATIO = 2.4;
const TRAIL_BANDS = 8;
const OUTLINE_SHADOW = 0x10222d;

// Fixed geometry keeps large projectiles under Pixi's automatic batching
// budget too: curve/ellipse tessellation otherwise grows with their radius.
function ellipsePoints(count: number): readonly (readonly [number, number])[] {
  return Array.from({ length: count }, (_, i) => {
    const angle = (i * Math.PI * 2) / count;
    return [Math.cos(angle), Math.sin(angle)] as const;
  });
}
const HALO_POINTS = ellipsePoints(12);
const HOT_CENTER_POINTS = ellipsePoints(8);

function polygonEllipse(
  g: Graphics,
  x: number,
  y: number,
  rx: number,
  ry: number,
  points = HALO_POINTS,
): Graphics {
  return g.poly(points.flatMap(([px, py]) => [x + px * rx, y + py * ry]));
}

/** Mix a highlight/shadow from the spell palette without adding per-spell draw branches. */
function tint(color: number, toward: number, amount: number): number {
  const channel = (shift: number): number => {
    const from = (color >> shift) & 0xff;
    return Math.round(from + (((toward >> shift) & 0xff) - from) * amount);
  };
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}

function corePath(g: Graphics, shape: ProjectileVisualProfile["shape"], r: number): Graphics {
  switch (shape) {
    case "shard":
      // Unequal facets give the shard a pointed leading edge and a chipped rear.
      return g.poly([
        CORE_LENGTH_RATIO * r,
        0,
        -0.65 * r,
        -0.78 * r,
        -CORE_LENGTH_RATIO * r,
        -0.12 * r,
        -1.3 * r,
        0.86 * r,
      ]);
    case "orb":
      // A rounded, eight-point flame head stays within the original radius.
      return g.poly([
        -r,
        0,
        -0.5 * r,
        -0.4 * r,
        0.35 * r,
        -0.9 * r,
        0.84 * r,
        -0.51 * r,
        0.99 * r,
        0,
        0.84 * r,
        0.51 * r,
        0.35 * r,
        0.9 * r,
        -0.5 * r,
        0.4 * r,
      ]);
    case "bolt":
      return g.poly([
        CORE_LENGTH_RATIO * r,
        0,
        1.3 * r,
        -0.62 * r,
        -CORE_LENGTH_RATIO * r,
        -0.48 * r,
        -1.65 * r,
        0,
        -CORE_LENGTH_RATIO * r,
        0.48 * r,
        1.3 * r,
        0.62 * r,
      ]);
  }
}

function trail(g: Graphics, profile: ProjectileVisualProfile, r: number): void {
  const fire = profile.shape === "orb";
  const start = -(fire ? 0.65 : CORE_LENGTH_RATIO - 0.3) * r;
  const length = profile.trailLengthRadii * r;
  const halfWidth = (fire ? 0.9 : profile.shape === "shard" ? 0.32 : 0.24) * r;
  const width = (t: number): number =>
    halfWidth * (1 - t) * (fire ? 1 + 0.6 * Math.sin(Math.PI * t) : 1);
  const center = (t: number): number => (fire ? Math.sin(t * Math.PI * 2) * 0.12 * r * (1 - t) : 0);

  // Adjacent, non-overlapping strips approximate an alpha ramp. They stay
  // vector-only (no per-projectile textures or canvas/gradient allocations).
  for (let band = 0; band < TRAIL_BANDS; band++) {
    const near = band / TRAIL_BANDS;
    const far = (band + 1) / TRAIL_BANDS;
    const alpha = profile.trailAlpha * (1 - (near + far) / 2) ** 1.6;
    const nearX = start - length * near;
    const farX = start - length * far;
    const nearY = center(near);
    const farY = center(far);
    g.poly([
      nearX,
      nearY - width(near),
      farX,
      farY - width(far),
      farX,
      farY + width(far),
      nearX,
      nearY + width(near),
    ]).fill({ color: profile.trailColor, alpha });
    // A narrow luminous spine adds depth without widening the opaque core.
    g.poly([
      nearX,
      nearY - width(near) * 0.28,
      farX,
      farY - width(far) * 0.28,
      farX,
      farY + width(far) * 0.28,
      nearX,
      nearY + width(near) * 0.28,
    ]).fill({ color: tint(profile.coreColor, 0xffffff, 0.65), alpha: alpha * 0.55 });
  }
}

/** Bounded vector appearance; position and heading are applied by the view. */
export function drawProjectileGraphics(
  g: Graphics,
  profile: ProjectileVisualProfile,
  radius: number,
  allegianceColor: number,
  moving: boolean,
): void {
  const r = radius;
  g.clear();
  if (moving) trail(g, profile, r);

  if (profile.shape === "orb") {
    // Translucent halos supply presence without enlarging the solid hit core.
    polygonEllipse(g, 0, 0, r * 1.6, r * 1.4).fill({ color: profile.trailColor, alpha: 0.06 });
    polygonEllipse(g, 0, 0, r * 1.2, r * 1.1).fill({ color: profile.coreColor, alpha: 0.12 });
  }

  // The outer team rim is separated from the spell by a dark inner edge.
  // Painting the body last keeps both accents outside its lit interior.
  corePath(g, profile.shape, r).stroke({ width: 3.5, color: allegianceColor, join: "bevel" });
  corePath(g, profile.shape, r).stroke({ width: 1.25, color: OUTLINE_SHADOW, join: "bevel" });
  corePath(g, profile.shape, r).fill({ color: profile.coreColor });
  const highlight = tint(profile.coreColor, 0xffffff, 0.8);
  const shadow = tint(profile.coreColor, 0x102637, 0.35);
  switch (profile.shape) {
    case "shard":
      g.poly([2.4 * r, 0, -1.3 * r, 0.86 * r, -0.6 * r, 0.05 * r]).fill({ color: shadow });
      g.poly([2.4 * r, 0, -0.65 * r, -0.78 * r, -1.7 * r, -0.14 * r, -0.2 * r, -0.12 * r]).fill({
        color: highlight,
      });
      break;
    case "orb":
      g.poly([
        -0.7 * r,
        0.2 * r,
        0.2 * r,
        0.6 * r,
        0.92 * r,
        0.15 * r,
        0.6 * r,
        0.7 * r,
        0.2 * r,
        0.8 * r,
      ]).fill({ color: shadow });
      polygonEllipse(g, 0.23 * r, -0.12 * r, 0.46 * r, 0.4 * r, HOT_CENTER_POINTS).fill({
        color: highlight,
      });
      break;
    case "bolt":
      g.poly([-1.65 * r, 0, 2.4 * r, 0, 1.3 * r, 0.62 * r, -2.4 * r, 0.48 * r]).fill({
        color: shadow,
      });
      g.poly([-1.8 * r, -0.18 * r, 1.3 * r, -0.35 * r, 2.15 * r, 0, -1.65 * r, 0]).fill({
        color: highlight,
      });
      break;
  }
}
